import { useEffect } from "react";

import { useQuery } from "@tanstack/react-query";
import Hls from "hls.js";
import { isEmpty } from "lodash/fp";
import { useParams } from "react-router-dom";

import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import useRecordingInfo from "@app/analytics/hooks/recordings/useRecordingInfo";
import useHighlightsQuery from "@app/analytics/MeetingReport/hooks/useHighlightsQuery";
import useMeetingInfoQuery from "@app/analytics/MeetingReport/hooks/useMeetingInfoQuery";
import useVideoStore from "@app/analytics/MeetingReport/stores/video";
import { getTrailerUrl } from "@app/analytics/MeetingReport/utils/videoUtils";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import { UnauthorizedVideoRecording, VideoRecording } from "@app/common/types";

import useMeetingReport from "./useMeetingReport";

interface UseVideoRecording {
  hasHighlightReel: boolean;
  hasTrailer: boolean;
  hasVideoRecording: boolean;
  isInitialLoading: boolean;
  trailerUrl: string | null;
  videoRecording?: VideoRecording | UnauthorizedVideoRecording;
}

const DEFAULT_OPTS = { enabled: true };

const getTrailerUrlWithPermissions = async (
  videoRecording?: VideoRecording | UnauthorizedVideoRecording
) => {
  const url = getTrailerUrl(videoRecording);
  if (!url) return url;
  try {
    const resp = await fetch(url);
    return resp.status === 200 ? url : null;
  } catch (err) {
    return null;
  }
};

const useVideoRecording = (opts = DEFAULT_OPTS): UseVideoRecording => {
  const { sessionId } = useParams();

  const { subscription } = useBillingContext();
  const { canAccessPlayback } = useFeatureAccess();
  const { meetingStartTime } = useMeetingReport(opts);
  const { data: sessionInfo } = useMeetingInfoQuery(sessionId, opts);
  const { data: highlights, isLoading: isHighlightsLoading } =
    useHighlightsQuery(opts);

  // assume recording is available unless the session info specifically
  // indicates that it is not
  const recordingEnabled = sessionInfo?.recordingEnabled ?? true;

  const {
    setPlaybackEnabled,
    setTrailerUrl,
    videoStartTimeOffset,
    setVideoStartTimeOffset,
    setVideoStartTime,
  } = useVideoStore((state) => ({
    setPlaybackEnabled: state.setPlaybackEnabled,
    setTrailerUrl: state.setTrailerUrl,
    videoStartTimeOffset: state.videoStartTimeOffset,
    setVideoStartTimeOffset: state.setVideoStartTimeOffset,
    setVideoStartTime: state.setVideoStartTime,
  }));

  const enabled = opts?.enabled && recordingEnabled;

  const {
    hasRecording,
    isInitialLoading: isVideoRecordingLoading,
    recordingInfo,
  } = useRecordingInfo(sessionId, {
    // even if the user can't access the playback on their current browser, we still want to
    // show accurate messaging re: whether a recording exists for this session
    attemptOnUnsupportedBrowser: true,
    enabled,
  });

  // the only way this combination would be true is if a workspace is on the enterprise plan but has
  // explicitly disabled playback. in all other cases, we want to allow the user to view the recording
  // regardless of their user access permissions/settings, since the video could be sponsored by
  // an enterprise user.
  const recordingDisabledForWorkspace =
    subscription.settings.storeRecording && !canAccessPlayback;

  useEffect(() => {
    if (hasRecording) {
      setPlaybackEnabled(!recordingDisabledForWorkspace && Hls.isSupported());

      if (meetingStartTime) {
        const diffBetweenMeetingStartAndVideoStart = meetingStartTime
          .diff(recordingInfo!.startTime)
          .as("seconds");

        if (videoStartTimeOffset !== diffBetweenMeetingStartAndVideoStart) {
          setVideoStartTimeOffset(diffBetweenMeetingStartAndVideoStart);
          setVideoStartTime(recordingInfo!.startTime);
        }
      }
    }
  }, [
    hasRecording,
    meetingStartTime,
    videoStartTimeOffset,
    recordingDisabledForWorkspace,
  ]);

  const trailerUrlQuery = useQuery({
    queryKey: ["trailerUrl", sessionId],
    queryFn: () => getTrailerUrlWithPermissions(recordingInfo),
    enabled: enabled && hasRecording,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
    retryOnMount: false,
  });

  const trailerUrl = trailerUrlQuery.data ?? null;

  useEffect(() => {
    // in an effect instead of query callback so that even when the above query
    // is cached, we still do this when the report is loaded
    setTrailerUrl(trailerUrl);
  }, [trailerUrl]);

  return {
    hasHighlightReel: !isEmpty(highlights),
    hasTrailer: trailerUrl != null,
    hasVideoRecording: hasRecording,
    isInitialLoading:
      isVideoRecordingLoading ||
      trailerUrlQuery.isLoading ||
      isHighlightsLoading,
    trailerUrl,
    videoRecording: recordingInfo,
  };
};

export default useVideoRecording;
